import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, X, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { searchProducts, notStockedSuggestion } from "../lib/productSearch";

let cache = null;

export default function HeaderSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [products, setProducts] = useState(cache || []);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { setOpen(false); setQ(""); }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    if (!cache) api.get("/products").then((r) => { cache = r.data; setProducts(r.data); }).catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 30);
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const results = searchProducts(products, q).slice(0, 6);
  const alt = q.trim() ? notStockedSuggestion(q) : null;
  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  const submit = (e) => { e.preventDefault(); if (results.length === 1) go(`/products/${results[0].sku}`); else if (q.trim()) go(`/products?q=${encodeURIComponent(q.trim())}`); };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-10 h-10 border border-[#00C2D6]/30 hover:border-[#00C2D6] text-[#F7FAFC] transition-colors rounded-sm"
        aria-label="Search products"
        data-testid="header-search-toggle"
      >
        {open ? <X size={18} /> : <Search size={18} />}
      </button>

      {open && (
        <div
          className="fixed inset-0 top-[68px] sm:top-[72px] z-50"
          style={{ background: "rgba(6, 12, 22, 0.88)", backdropFilter: "blur(8px)" }}
          onClick={() => setOpen(false)}
          data-testid="header-search-panel"
        >
          <div className="bg-[#0B1220] border-t border-b border-[#00C2D6]/20" onClick={(e) => e.stopPropagation()}>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5">
              <form onSubmit={submit} className="relative">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#00C2D6] pointer-events-none" />
                <input
                  ref={inputRef}
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search the catalogue — Retatrutide, GHK-Cu, bac water…"
                  className="w-full bg-[#060C16] border border-[#00C2D6]/30 focus:border-[#00C2D6] rounded-sm pl-11 pr-4 py-3.5 text-base text-[#F7FAFC] placeholder-[#5B6776] outline-none"
                  autoComplete="off"
                  data-testid="header-search-input"
                  aria-label="Search products"
                />
              </form>

              {q.trim() && (
                <div className="mt-3" data-testid="header-search-results">
                  {results.map((p) => (
                    <button
                      key={p.sku}
                      type="button"
                      onClick={() => go(`/products/${p.sku}`)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-left hover:bg-[#111A2B] transition-colors"
                      data-testid={`header-search-result-${p.sku}`}
                    >
                      <div className="w-10 h-10 shrink-0 rounded-sm bg-[#111A2B] overflow-hidden">
                        {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[#F7FAFC] text-sm font-semibold truncate">
                          {p.name} <span className="text-[#9CA8B8] font-normal">{p.strength}</span>
                        </div>
                        <div className="pg-mono text-[10px] uppercase tracking-[0.16em] text-[#9CA8B8]">
                          {p.stock_status === "out_of_stock" ? <span className="text-[#FF8A5B]">Out of stock</span> : <span className="text-[#A6FF00]">In UK stock</span>}
                        </div>
                      </div>
                      <div className="text-[#A6FF00] font-bold text-sm whitespace-nowrap">£{Number(p.price).toFixed(2)}</div>
                    </button>
                  ))}

                  {results.length === 0 && !alt && (
                    <div className="px-3 py-4 text-sm text-[#9CA8B8]" data-testid="header-search-empty">
                      Nothing matches &ldquo;{q}&rdquo;. <button type="button" onClick={() => go("/products")} className="text-[#00C2D6] underline underline-offset-2">Browse the full catalogue</button>
                    </div>
                  )}

                  {alt && (
                    <button
                      type="button"
                      onClick={() => go(`/products?q=${encodeURIComponent(alt.suggest)}`)}
                      className="w-full mt-1 px-3 py-3 rounded-sm border border-[#A6FF00]/40 bg-[#A6FF00]/[0.06] text-left hover:bg-[#A6FF00]/10 transition-colors"
                      data-testid="header-search-alt"
                    >
                      <div className="text-sm text-[#F7FAFC]">We don&rsquo;t stock {alt.asked}. Researchers in that area order <strong className="text-[#A6FF00]">{alt.suggest}</strong> &mdash; five sizes, Buy 3 Get 1 Free.</div>
                    </button>
                  )}

                  {results.length > 0 && (
                    <button
                      type="button"
                      onClick={() => go(`/products?q=${encodeURIComponent(q.trim())}`)}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 pg-mono text-[10px] uppercase tracking-[0.18em] text-[#00C2D6] hover:text-[#F7FAFC]"
                      data-testid="header-search-view-all"
                    >
                      View all in catalogue <ArrowRight size={12} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
